import axios from 'axios';
import { BASE_URL } from './helpers';
import {
  AIModel,
  ModelConstraintsResponse,
  ModelsResponse,
  ModelType,
  ModelConstraintsRawResponse
} from './structs/model';

// Typed reader for a video model's registry capabilities — re-exported here
// so consumers that already import `ModelsAPI` from this module can pull the
// type from the same place. `parseVideoCapabilities`/`excluded` live in
// `./videoCapabilities` and are imported directly.
export type { ReferenceKind, VideoCapabilities } from './videoCapabilities';

// ==================== Params ====================

interface GetModelsParams {
  provider?: string;
  type?: ModelType;
}

// ==================== Helpers ====================

const dedupeById = (models: AIModel[]): AIModel[] =>
  Array.from(new Map(models.map((m) => [m.id, m])).values());

const getProviderResponseKey = (provider: string): string | undefined => {
  if (provider === 'openrouter') return 'openrouter';
  if (provider === 'runware') return 'runware';
  if (provider === 'suno') return 'suno';
  if (provider === 'vertexai') return 'vertexai';
  if (provider === 'profile') return 'profile';
  if (provider === 'anthropic') return 'anthropic';
  return undefined;
};

// ==================== Constraints cache ====================

/**
 * Keyed by `model::modality`, holding the in-flight PROMISE rather than only
 * the settled value.
 *
 * Caching the resolved value alone collapses nothing on a cold start: a canvas
 * with N generator nodes on one model, or a slot map with N voice slots, mounts
 * N effects in the same tick, every one of them misses the still-empty cache,
 * and the registry gets N identical GETs. Caching the promise makes callers two
 * through N await the first one's request.
 *
 * A REJECTED request evicts its own entry, so a momentary outage is not
 * remembered as this model's answer for the rest of the session.
 */
const constraintsCache = new Map<string, Promise<ModelConstraintsResponse>>();

// ==================== API ====================

const getModels = async (params: GetModelsParams = {}): Promise<ModelsResponse> => {
  const queryParams = new URLSearchParams();
  if (params.provider) queryParams.set('provider', params.provider);
  if (params.type) queryParams.set('type', params.type);

  const query = queryParams.toString();
  const url = query ? `${BASE_URL}/models?${query}` : `${BASE_URL}/models`;

  return axios.get(url).then((res) => res.data);
};

const getModelsForProvider = async (
  provider: string,
  type?: ModelType
): Promise<AIModel[]> => {
  const response = await getModels({ provider, type });
  const responseKey = getProviderResponseKey(provider);

  const recommended = (response.recommended || []).filter(
    (m) => m.provider === provider && (!type || m.type === type)
  );
  const providerModels = responseKey ? (response[responseKey] || []) : [];

  return dedupeById([...recommended, ...providerModels]);
};

// Chat models span providers: OpenRouter's catalog, Vertex AI's native Gemini
// chat models (bare `gemini-*` ids, served under the `vertexai` key), Modal
// Endpoints chat models (agent builder candidates, `modal_endpoints`) and
// Anthropic's own (bare `claude-*` ids, `anthropic`; only on a server with its key).
const getChatModels = async (): Promise<AIModel[]> => {
  const response = await getModels({ type: 'chat' });

  const recommended = (response.recommended || []).filter((m) => m.type === 'chat');
  const openrouter = (response.openrouter || []).filter((m) => m.type === 'chat');
  const vertexai = (response.vertexai || []).filter((m) => m.type === 'chat');
  const modalEndpoints = (response.modal_endpoints || []).filter((m) => m.type === 'chat');
  const anthropic = (response.anthropic || []).filter((m) => m.type === 'chat');

  return dedupeById([...recommended, ...openrouter, ...vertexai, ...modalEndpoints, ...anthropic]);
};

// Video-input chat models: those the registry classifies as `chat` (they emit
// text) whose input modalities include "video" — the live equivalent of
// OpenRouter's `?input_modalities=video`. Used by the Builder's video analysis
// setting; the backend independently enforces the same capability.
const getVideoInputModels = async (): Promise<AIModel[]> => {
  const response = await getModels({ type: 'chat' });

  const candidates = [
    ...(response.recommended || []),
    ...(response.openrouter || []),
    ...(response.vertexai || []),
  ].filter((m) => m.type === 'chat');

  const videoCapable = candidates.filter((m) =>
    (m.capabilities?.input_modalities || []).some((mod) => mod.toLowerCase() === 'video')
  );

  return dedupeById(videoCapable);
};

// Vision chat models: registry `chat` entries (they emit text) whose input
// modalities include "image". Used by the Reviewer settings — a judge has to be
// able to LOOK at the candidate still it is grading. `agents` adds the models
// only agents can run (Anthropic's own): the /chat operator's thread model
// picker passes it, the judges (whose backend cannot dispatch them) do not.
const getImageInputModels = async (agents = false): Promise<AIModel[]> => {
  const response = await getModels({ type: 'chat' });

  const candidates = [
    ...(response.recommended || []),
    ...(response.openrouter || []),
    ...(response.vertexai || []),
    ...(agents ? response.anthropic || [] : []),
  ].filter((m) => m.type === 'chat');

  const visionCapable = candidates.filter((m) =>
    (m.capabilities?.input_modalities || []).some((mod) => mod.toLowerCase() === 'image')
  );

  return dedupeById(visionCapable);
};

const getImageModels = async (provider?: string): Promise<AIModel[]> => {
  if (provider) return getModelsForProvider(provider, 'image');

  const response = await getModels({ type: 'image' });

  const recommended = (response.recommended || []).filter((m) => m.type === 'image');
  const openrouter = (response.openrouter || []).filter((m) => m.type === 'image');
  const runware = (response.runware || []).filter((m) => m.type === 'image');
  const vertexai = (response.vertexai || []).filter((m) => m.type === 'image');
  const profile = (response.profile || []).filter((m) => m.type === 'image');

  return dedupeById([...recommended, ...openrouter, ...runware, ...vertexai, ...profile]);
};

const getVideoModels = async (provider?: string): Promise<AIModel[]> => {
  if (provider) return getModelsForProvider(provider, 'video');

  const response = await getModels({ type: 'video' });

  const recommended = (response.recommended || []).filter((m) => m.type === 'video');
  const runware = (response.runware || []).filter((m) => m.type === 'video');
  const vertexai = (response.vertexai || []).filter((m) => m.type === 'video');

  return dedupeById([...recommended, ...runware, ...vertexai]);
};

const getAudioModels = async (provider?: string): Promise<AIModel[]> => {
  if (provider) return getModelsForProvider(provider, 'audio');

  const response = await getModels({ type: 'audio' });

  const recommended = (response.recommended || []).filter((m) => m.type === 'audio');
  const runware = (response.runware || []).filter((m) => m.type === 'audio');
  const suno = (response.suno || []).filter((m) => m.type === 'audio');

  return dedupeById([...recommended, ...runware, ...suno]);
};

const getNarratorModels = async (): Promise<AIModel[]> => {
  return axios.get(`${BASE_URL}/models/narrator`).then((res) => res.data.models ?? []);
};

/**
 * Written as a plain promise chain rather than async/await on purpose: several
 * consumers settle their `useState` off this, and every extra `await` here is
 * another microtask before that state lands.
 */
const fetchModelConstraints = (
  modelId: string,
  modality: 'image' | 'video' | 'audio'
): Promise<ModelConstraintsResponse> =>
  axios
    .get<ModelConstraintsRawResponse>(
      `${BASE_URL}/models/${encodeURIComponent(modelId)}/constraints`
    )
    .then((res) => {
      const raw = res.data;
      // Extract the modality-specific fields block
      const modalityBlock = raw.constraints?.[modality];
      return {
        model_id: raw.model_id,
        fields: modalityBlock?.fields ?? {},
        capabilities:
          typeof modalityBlock?.capabilities === 'object' && modalityBlock.capabilities !== null
            ? (modalityBlock.capabilities as Record<string, unknown>)
            : {},
        provider_settings:
          typeof raw.provider_settings === 'object' && raw.provider_settings !== null
            ? (raw.provider_settings as Record<string, unknown>)
            : undefined,
      };
    });

const getModelConstraints = (
  modelId: string,
  modality: 'image' | 'video' | 'audio' = 'image',
  useCache = true
): Promise<ModelConstraintsResponse> => {
  const cacheKey = `${modelId}::${modality}`;
  if (useCache) {
    const pending = constraintsCache.get(cacheKey);
    if (pending) return pending;
  }

  // Stored BEFORE the request settles — that is the whole point: the callers
  // that mount in the same tick find this entry and await it instead of firing
  // their own GET.
  const request = fetchModelConstraints(modelId, modality);
  // Eviction is attached as a SIDE BRANCH, not spliced into the chain callers
  // await: `return request.catch(...)` would hand every caller a promise one
  // microtask later than the request itself. Rejection stays visible to them,
  // because they get `request`.
  request.catch(() => {
    if (constraintsCache.get(cacheKey) === request) constraintsCache.delete(cacheKey);
  });
  constraintsCache.set(cacheKey, request);
  return request;
};

const clearConstraintsCache = () => constraintsCache.clear();

// ==================== Export ====================

const ModelsAPI = {
  getModels,
  getModelsForProvider,
  getChatModels,
  getVideoInputModels,
  getImageInputModels,
  getImageModels,
  getVideoModels,
  getAudioModels,
  getNarratorModels,
  getModelConstraints,
  clearConstraintsCache,
};

export default ModelsAPI;
